/**
 * Build a Box: quantity +/- , selling plan, tier discount, total, add to cart.
 */
(function () {
  const routes = window.routes || { cart_add_url: '/cart/add.js', cart_url: '/cart' };

  /**
   * Shopify-compatible money formatter. Mirrors the output of the Liquid `money` filter
   * so server-rendered prices and JS-updated prices stay visually identical (no flicker
   * on refresh, no currency symbol disappearing).
   */
  function formatMoney(cents, format) {
    const value = parseInt(cents, 10) || 0;
    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const main = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const frac = parts[1] ? decimal + parts[1] : '';
      return main + frac;
    }

    const match = formatString.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';
    let formatted;
    switch (placeholder) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDelimiters(value, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.'); break;
      case 'amount':
      default:
        formatted = withDelimiters(value, 2, ',', '.'); break;
    }
    return formatString.replace(placeholderRegex, formatted);
  }

  const BOX_QUERY_PARAM = 'box';
  const SUB_QUERY_PARAM = 'sub';

  /** Parse ?box=handle1:qty1,handle2:qty2 into { handle1: qty1, handle2: qty2 }. Order in URL does not matter. */
  function parseBoxFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const boxStr = urlParams.get(BOX_QUERY_PARAM);
    if (!boxStr || typeof boxStr !== 'string') return {};
    const out = {};
    boxStr.split(',').forEach((part) => {
      const idx = part.lastIndexOf(':');
      if (idx === -1) return;
      const handle = part.slice(0, idx).trim();
      const qty = parseInt(part.slice(idx + 1), 10);
      if (handle && !isNaN(qty) && qty >= 0) out[handle] = qty;
    });
    return out;
  }

  /** Parse ?sub=selling_plan_id. Empty or non-numeric = one-time. */
  function getSubFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const sub = urlParams.get(SUB_QUERY_PARAM);
    if (sub == null || sub === '') return '';
    const num = parseInt(sub, 10);
    return !isNaN(num) && num > 0 ? String(num) : '';
  }

  /** Apply URL state to widget: set quantities by handle, set purchase type and selling plan from sub. */
  function applyUrlToWidget(container) {
    const boxByHandle = parseBoxFromUrl();
    const subFromUrl = getSubFromUrl();
    const hasBoxInUrl = Object.keys(boxByHandle).length > 0;

    if (hasBoxInUrl) {
      container.querySelectorAll('.build-a-box__row').forEach((row) => {
        const handle = row.dataset.productHandle;
        const input = row.querySelector('.build-a-box__qty-input');
        if (!input) return;
        const qty = handle != null && boxByHandle.hasOwnProperty(handle)
          ? Math.max(0, parseInt(boxByHandle[handle], 10) || 0)
          : 0;
        const min = parseInt(input.getAttribute('min'), 10) || 0;
        input.value = Math.max(min, qty);
      });
    }

    const subscriptionRadio = container.querySelector('.build-a-box__purchase-type-input[value="subscription"]');
    const onetimeRadio = container.querySelector('.build-a-box__purchase-type-input[value="onetime"]');
    const planSelect = container.querySelector('.build-a-box__plan-select[data-selling-plan-select]');

    if (subFromUrl && planSelect) {
      const option = Array.from(planSelect.options).find((opt) => opt.value === subFromUrl);
      if (option) {
        planSelect.value = subFromUrl;
        if (subscriptionRadio) subscriptionRadio.checked = true;
      } else if (onetimeRadio) {
        onetimeRadio.checked = true;
      }
    }
  }

  /** Current widget state: box string (handle:qty for qty > 0) and sub (selling plan id or ''). */
  function getWidgetState(container) {
    const parts = [];
    container.querySelectorAll('.build-a-box__row').forEach((row) => {
      const handle = row.dataset.productHandle;
      const input = row.querySelector('.build-a-box__qty-input');
      if (!handle || !input) return;
      const qty = parseInt(input.value, 10) || 0;
      if (qty > 0) parts.push(handle + ':' + qty);
    });
    const box = parts.length ? parts.join(',') : '';
    const purchaseTypeRadio = container.querySelector('.build-a-box__purchase-type-input:checked');
    const isSubscription = purchaseTypeRadio && purchaseTypeRadio.value === 'subscription';
    const planSelect = container.querySelector('.build-a-box__plan-select[data-selling-plan-select]');
    const sub = isSubscription && planSelect && planSelect.value ? planSelect.value : '';
    return { box, sub };
  }

  /** Update URL to match current widget state (replaceState). */
  function updateUrlFromWidget(container) {
    const { box, sub } = getWidgetState(container);
    const url = new URL(window.location.href);
    if (box) {
      url.searchParams.set(BOX_QUERY_PARAM, box);
    } else {
      url.searchParams.delete(BOX_QUERY_PARAM);
    }
    if (sub) {
      url.searchParams.set(SUB_QUERY_PARAM, sub);
    } else {
      url.searchParams.delete(SUB_QUERY_PARAM);
    }
    window.history.replaceState({}, '', url.toString());
  }

  function parseMoney(el) {
    if (!el || !el.textContent) return 0;
    const match = el.textContent.replace(/\s/g, '').match(/[\d.,]+/);
    return match ? parseFloat(match[0].replace(',', '.')) * 100 : 0;
  }

  function getConfig(container) {
    const sectionId = container.dataset.sectionId;
    const script = document.getElementById(`build-a-box-data-${sectionId}`);
    if (!script || !script.textContent) return null;
    try {
      const config = JSON.parse(script.textContent);
      if (config.galleryCombosJson && typeof config.galleryCombosJson === 'string') {
        try {
          const list = JSON.parse(config.galleryCombosJson);
          config.galleryImageByComboKey = {};
          if (Array.isArray(list)) {
            list.forEach((entry) => {
              if (entry && Array.isArray(entry.handles) && entry.imageUrl) {
                const key = entry.handles.slice().sort().join(',');
                config.galleryImageByComboKey[key] = entry.imageUrl;
              }
            });
          }
          delete config.galleryCombosJson;
        } catch (e) {
          delete config.galleryImageByComboKey;
        }
      }
      return config;
    } catch (e) {
      return null;
    }
  }

  function getVariantData(config, variantId) {
    if (!config?.variants) return null;
    const id = parseInt(variantId, 10);
    return config.variants.find((v) => parseInt(v.variantId, 10) === id) || null;
  }

  function getUnitPrice(variantData, sellingPlanId) {
    if (!variantData) return 0;
    const basePrice = parseInt(variantData.price, 10) || 0;
    if (sellingPlanId && variantData.sellingPlanAllocations?.length) {
      const alloc = variantData.sellingPlanAllocations.find(
        (a) => String(a.sellingPlanId) === String(sellingPlanId)
      );
      if (alloc && alloc.price != null) return parseInt(alloc.price, 10) || 0;
    }
    return basePrice;
  }

  /**
   * Returns the applicable tier for the given count. Works with any number of tiers.
   * Tiers are compared by threshold (or productsCount) descending; the first tier
   * where count >= threshold wins (e.g. 2 lines → 10%, 3+ lines → 15%).
   */
  function getTierForCount(tiers, count) {
    if (!tiers || !Array.isArray(tiers) || count < 1) return null;
    const getThreshold = (t) => (parseInt(t.threshold, 10) || parseInt(t.productsCount, 10) || 0);
    const sorted = [...tiers].sort((a, b) => getThreshold(b) - getThreshold(a));
    for (const tier of sorted) {
      const threshold = getThreshold(tier);
      if (threshold > 0 && count >= threshold) return tier;
    }
    return null;
  }

  function getSelectedSellingPlanId(container) {
    const purchaseTypeRadio = container.querySelector('.build-a-box__purchase-type-input:checked');
    if (!purchaseTypeRadio || purchaseTypeRadio.value !== 'subscription') return '';
    const select = container.querySelector('.build-a-box__plan-select[data-selling-plan-select]');
    return select ? (select.value || '') : '';
  }

  function getSubscriptionPlanId(container) {
    const select = container.querySelector('.build-a-box__plan-select[data-selling-plan-select]');
    return select ? (select.value || '') : '';
  }

  function updateRecommendedLabel(container, itemCount) {
    const select = container.querySelector('.build-a-box__plan-select[data-selling-plan-select]');
    if (!select) return;
    const options = Array.from(select.querySelectorAll('option'));
    if (options.length === 0) return;
    const recommendedIndex = Math.min(itemCount >= 2 ? 3 : 2, options.length);
    options.forEach((option, i) => {
      const baseName = option.dataset.baseName || option.getAttribute('data-base-name') || option.textContent.replace(/\s*-\s*Rekomendujemy\s*$/, '').trim();
      option.textContent = baseName + (i + 1 === recommendedIndex ? ' - Rekomendujemy' : '');
    });
  }

  const DEFAULT_THRESHOLD_1 = 8900;
  const DEFAULT_THRESHOLD_2 = 17800;
  const DEFAULT_THRESHOLD_3 = 17900;

  function updateFreeDeliveryText(container, config, cartTotalCents, boxTotalCents) {
    const wrapper = container.querySelector('[data-free-delivery-container]');
    if (!wrapper) return;
    const tiers = wrapper.querySelectorAll('[data-free-delivery-tier]');
    if (tiers.length < 3) return;
    const t1 = config?.freeDeliveryThreshold1 != null ? parseInt(config.freeDeliveryThreshold1, 10) : DEFAULT_THRESHOLD_1;
    const t2 = config?.freeDeliveryThreshold2 != null ? parseInt(config.freeDeliveryThreshold2, 10) : DEFAULT_THRESHOLD_2;
    const t3 = config?.freeDeliveryThreshold3 != null ? parseInt(config.freeDeliveryThreshold3, 10) : DEFAULT_THRESHOLD_3;
    const combined = (parseInt(cartTotalCents, 10) || 0) + (parseInt(boxTotalCents, 10) || 0);
    let tier;
    if (combined < t1) tier = 1;
    else if (combined < t3) tier = 2;
    else tier = 3;
    tiers.forEach((el) => {
      const t = parseInt(el.getAttribute('data-free-delivery-tier'), 10);
      el.classList.toggle('hidden', t !== tier);
    });
  }

  function updateTotals(container, config, isInitial = false) {
    const minProducts = config?.minProductsCount ?? 1;
    const discountType = (config?.discountType || 'PERCENTAGE').toUpperCase();
    const inputs = container.querySelectorAll('.build-a-box__qty-input');
    const sellingPlanId = getSelectedSellingPlanId(container);
    const subscriptionPlanId = getSubscriptionPlanId(container);

    let oneTimeSubtotal = 0;
    let subscriptionSubtotal = 0;
    let linesCount = 0;
    let totalQuantity = 0;

    inputs.forEach((input) => {
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) return;
      linesCount += 1;
      totalQuantity += qty;
      const variantId = input.dataset.variantId;
      const variantData = getVariantData(config, variantId);
      if (!variantData) return;
      const unitOneTime = parseInt(variantData.price, 10) || 0;
      oneTimeSubtotal += unitOneTime * qty;
      subscriptionSubtotal += getUnitPrice(variantData, subscriptionPlanId) * qty;
    });

    // Use total quantity only when explicitly TOTAL_QUANTITY; otherwise use number of lines (distinct products).
    const useTotalQuantity =
      String(config?.thresholdType ?? '').toUpperCase().trim() === 'TOTAL_QUANTITY';
    const countForTier = totalQuantity;
    const tier = getTierForCount(config?.tiers, countForTier);
    const tierValue = tier ? parseInt(tier.value, 10) || 0 : 0;
    let oneTimeDiscount = 0;
    let subscriptionDiscount = 0;
    if (discountType === 'PERCENTAGE') {
      oneTimeDiscount = Math.round((oneTimeSubtotal * tierValue) / 100);
      subscriptionDiscount = Math.round((subscriptionSubtotal * tierValue) / 100);
    } else {
      oneTimeDiscount = Math.min(tierValue, oneTimeSubtotal);
      subscriptionDiscount = Math.min(tierValue, subscriptionSubtotal);
    }
    const oneTimeTotal = oneTimeSubtotal - oneTimeDiscount;
    const subscriptionTotal = subscriptionSubtotal - subscriptionDiscount;

    const onetimeTotalEl = container.querySelector('[data-onetime-total]');
    const onetimeSubtotalEl = container.querySelector('[data-onetime-subtotal]');
    const subscriptionTotalEl = container.querySelector('[data-subscription-total]');
    const subscriptionSubtotalEl = container.querySelector('[data-subscription-subtotal]');

    const moneyFmt = config?.moneyFormat;
    if (onetimeTotalEl) {
      onetimeTotalEl.textContent = formatMoney(oneTimeTotal, moneyFmt);
      const onetimeWrap = onetimeTotalEl.closest('.build-a-box__plan-price-wrap');
      if (onetimeWrap) onetimeWrap.classList.toggle('hidden', oneTimeTotal === 0);
    }
    if (onetimeSubtotalEl) {
      if (oneTimeDiscount > 0 && oneTimeSubtotal > 0) {
        onetimeSubtotalEl.textContent = formatMoney(oneTimeSubtotal, moneyFmt);
        onetimeSubtotalEl.classList.remove('hidden');
      } else {
        onetimeSubtotalEl.classList.add('hidden');
      }
    }
    if (subscriptionTotalEl) {
      subscriptionTotalEl.textContent = formatMoney(subscriptionTotal, moneyFmt);
      const subscriptionWrap = subscriptionTotalEl.closest('.build-a-box__plan-price-wrap');
      if (subscriptionWrap) subscriptionWrap.classList.toggle('hidden', subscriptionTotal === 0);
    }
    if (subscriptionSubtotalEl) {
      if (subscriptionTotal > 0 && oneTimeTotal > subscriptionTotal) {
        subscriptionSubtotalEl.textContent = formatMoney(oneTimeTotal, moneyFmt);
        subscriptionSubtotalEl.classList.remove('hidden');
      } else {
        subscriptionSubtotalEl.classList.add('hidden');
      }
    }

    const addBtn = container.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.disabled = linesCount < minProducts;
    }

    updateRecommendedLabel(container, totalQuantity);

    const cartTotalCents = parseInt(container.dataset.cartTotal, 10) || 0;
    const isSubscription = !!getSelectedSellingPlanId(container);
    const boxTotalCents = isSubscription ? subscriptionTotal : oneTimeTotal;
    updateFreeDeliveryText(container, config, cartTotalCents, boxTotalCents);

    updateGallery(container, config, isInitial);

    return { oneTimeTotal, subscriptionTotal, linesCount };
  }

  // Cache of product handle -> [imageUrl, ...] so switching back and forth
  // between single products does not re-hit the network.
  const productImagesCache = {};
  const productImageDimensionsCache = {};

  /** Add/override a `width` query param on a Shopify CDN image URL. */
  function withImageWidth(src, width) {
    if (!src) return src;
    let normalized = src;
    if (normalized.indexOf('//') === 0) normalized = 'https:' + normalized;
    try {
      const url = new URL(normalized, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return normalized;
    }
  }

  /** Fetch a product's full image gallery (in gallery order). Resolves to a URL array (cached). */
  function fetchProductImages(handle) {
    if (!handle) return Promise.resolve([]);
    if (productImagesCache[handle]) return Promise.resolve(productImagesCache[handle]);
    const rootRaw = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const root = rootRaw.endsWith('/') ? rootRaw : rootRaw + '/';
    const url = root + 'products/' + encodeURIComponent(handle) + '.js';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((productData) => {
        let images = [];
        const imageDimensions = {};
        if (Array.isArray(productData.media)) {
          images = productData.media
            .filter((m) => m.media_type === 'image' && m.src)
            .map((m) => {
              const src = withImageWidth(m.src, 1600);
              const width = Number(m.width || m.preview_image?.width);
              const height = Number(m.height || m.preview_image?.height);
              if (width > 0 && height > 0) imageDimensions[src] = { width, height };
              return src;
            });
        }
        if (images.length === 0 && Array.isArray(productData.images)) {
          images = productData.images.map((src) => withImageWidth(src, 1600));
        }
        productImageDimensionsCache[handle] = imageDimensions;
        productImagesCache[handle] = images;
        return images;
      })
      .catch(() => []);
  }

  /** Distinct product handles with quantity > 0, in row order. */
  function getActiveHandles(container) {
    const handles = [];
    container.querySelectorAll('.build-a-box__qty-input').forEach((input) => {
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) return;
      const row = input.closest('.build-a-box__row');
      const handle = row && row.dataset.productHandle;
      if (handle && handles.indexOf(handle) === -1) handles.push(handle);
    });
    return handles;
  }

  /** Handle of the product whose page we are on (its gallery is the default one). */
  function getCurrentProductHandle(container) {
    if (container.dataset.currentProductHandle) return container.dataset.currentProductHandle;
    const currentRow = container.querySelector('.build-a-box__row--current-product');
    if (currentRow && currentRow.dataset.productHandle) return currentRow.dataset.productHandle;
    return container.dataset.parentProductHandle || '';
  }

  /** Remove any combo-overlay image previously pushed onto the gallery. */
  function clearComboOverlay(container) {
    const lastUrl = container.dataset.lastGalleryImageUrl || '';
    if (lastUrl) {
      publish(PUB_SUB_EVENTS.removeImageFromGallerySlider, { imageUrl: lastUrl });
      container.dataset.lastGalleryImageUrl = '';
    }
  }

  /**
   * Decide what the gallery should show based on the current selection:
   *  - exactly one product, and it is another product than the page product ->
   *    replace the whole gallery with that product's full media gallery;
   *  - 2+ products with no combo image configured for that set -> replace the
   *    whole gallery with the LAST selected product's media gallery;
   *  - page product alone, or 2+ products with a combo image -> restore the base
   *    gallery and apply the combo-overlay behavior.
   */
  function updateGallery(container, config, isInitial = false) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const activeHandles = getActiveHandles(container);
    const currentHandle = getCurrentProductHandle(container);

    // Case 1: exactly one product, and it is not the page product.
    if (activeHandles.length === 1 && activeHandles[0] !== currentHandle) {
      replaceGalleryWithProduct(container, config, activeHandles[0]);
      return;
    }

    // Case 2: 2+ products but no combo image defined for this exact set ->
    // fall back to the full gallery of the last selected (still active) product.
    if (activeHandles.length >= 2) {
      const map = config?.galleryImageByComboKey;
      const comboKey = activeHandles.slice().sort().join(',');
      const comboImage = map && typeof map === 'object' ? map[comboKey] || null : null;
      if (!comboImage) {
        let target = container.dataset.lastSelectedHandle;
        if (!target || activeHandles.indexOf(target) === -1) {
          target = activeHandles[activeHandles.length - 1];
        }
        replaceGalleryWithProduct(container, config, target);
        return;
      }
    }

    // Case 3: page product alone, or a combo image exists -> invalidate any
    // pending replace, restore the base gallery if replaced, run combo overlay.
    container._galleryReq = (container._galleryReq || 0) + 1;
    if (container.dataset.galleryReplaceHandle) {
      container.dataset.galleryReplaceHandle = '';
      publish(PUB_SUB_EVENTS.restoreGallerySlides, {});
    }
    updateComboOverlay(container, config, isInitial);
  }

  /**
   * Fetch a product's full gallery and replace the whole product gallery with
   * it. Deduplicates against the currently shown product, shows the loading
   * spinner only on a real network fetch, and guards against out-of-order
   * responses. Shared by the single-product and no-combo fallback cases.
   */
  function replaceGalleryWithProduct(container, config, handle) {
    if (!handle) return;
    // Already showing this product's gallery? Nothing to do.
    if (container.dataset.galleryReplaceHandle === handle) return;

    clearComboOverlay(container);
    const seq = (container._galleryReq = (container._galleryReq || 0) + 1);
    container.dataset.galleryReplaceHandle = handle;

    // Only show the spinner when the gallery is actually fetched over the
    // network; cached products swap in instantly with no flash.
    const isCached = !!productImagesCache[handle];
    if (!isCached) {
      publish(PUB_SUB_EVENTS.setGalleryLoading, { loading: true });
    }

    fetchProductImages(handle).then((images) => {
      // A newer selection change superseded this fetch — drop the result.
      if (container._galleryReq !== seq) {
        publish(PUB_SUB_EVENTS.setGalleryLoading, { loading: false });
        return;
      }
      if (images && images.length) {
        publish(PUB_SUB_EVENTS.replaceGallerySlides, {
          images: images,
          handle: handle,
          imageDimensions: productImageDimensionsCache[handle],
        });
      } else {
        // No images resolved: fall back to the default gallery + combo.
        container.dataset.galleryReplaceHandle = '';
        publish(PUB_SUB_EVENTS.restoreGallerySlides, {});
        updateComboOverlay(container, config, true);
      }
      publish(PUB_SUB_EVENTS.setGalleryLoading, { loading: false });
    });
  }

  function updateComboOverlay(container, config, isInitial = false) {
    const map = config?.galleryImageByComboKey;
    if (!map || typeof map !== 'object') return;

    const selectedHandles = [];
    container.querySelectorAll('.build-a-box__qty-input').forEach((input) => {
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) return;
      const row = input.closest('.build-a-box__row');
      const handle = row && row.dataset.productHandle;
      if (handle) selectedHandles.push(handle);
    });
    const comboKey = selectedHandles.length ? selectedHandles.slice().sort().join(',') : '';
    const imageUrl = comboKey ? (map[comboKey] || null) : null;
    const lastUrl = container.dataset.lastGalleryImageUrl || '';

    if (lastUrl && lastUrl !== imageUrl) {
      publish(PUB_SUB_EVENTS.removeImageFromGallerySlider, { imageUrl: lastUrl });
    }
    if (imageUrl) {
      publish(PUB_SUB_EVENTS.addImageToGallerySlider, { imageUrl, isInitial });
    }
    container.dataset.lastGalleryImageUrl = imageUrl || '';
  }

  function updateLineTotals(container, config) {
    // No per-row line totals in DOM; kept for compatibility.
  }

  function updateRowStates(container) {
    container.querySelectorAll('.build-a-box__row').forEach((row) => {
      const input = row.querySelector('.build-a-box__qty-input');
      const qty = parseInt(input?.value, 10) || 0;
      row.classList.toggle('build-a-box__row--active', qty > 0);
    });
  }

  function bindQuantity(container, config) {
    container.querySelectorAll('.build-a-box__qty').forEach((qtyWrap) => {
      const input = qtyWrap.querySelector('.build-a-box__qty-input');
      const minus = qtyWrap.querySelector('.build-a-box__qty-minus');
      const plus = qtyWrap.querySelector('.build-a-box__qty-plus');
      if (!input || !minus || !plus) return;

      function change(delta) {
        const min = parseInt(input.getAttribute('min'), 10) || 0;
        let val = parseInt(input.value, 10) || 0;
        val = Math.max(min, val + delta);
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      minus.addEventListener('click', () => {
        const currentQty = parseInt(input.value, 10) || 0;
        if (currentQty <= 0) return;
        const totalQty = Array.from(container.querySelectorAll('.build-a-box__qty-input'))
          .reduce((sum, inp) => sum + (parseInt(inp.value, 10) || 0), 0);
        if (totalQty <= 1) return;
        change(-1);
      });
      plus.addEventListener('click', () => change(1));
      input.addEventListener('change', () => {
        const min = parseInt(input.getAttribute('min'), 10) || 0;
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < min) val = min;
        const otherQty = Array.from(container.querySelectorAll('.build-a-box__qty-input'))
          .reduce((sum, inp) => inp !== input ? sum + (parseInt(inp.value, 10) || 0) : sum, 0);
        if (val === 0 && otherQty === 0) val = 1;
        input.value = val;
        // Remember the most recently chosen (still positive) product so the
        // gallery can fall back to it when no combo image is configured.
        if (val > 0) {
          const changedRow = input.closest('.build-a-box__row');
          if (changedRow && changedRow.dataset.productHandle) {
            container.dataset.lastSelectedHandle = changedRow.dataset.productHandle;
          }
        }
        updateRowStates(container);
        updateLineTotals(container, config);
        updateTotals(container, config);
        updateUrlFromWidget(container);
      });
    });
  }

  function bindSellingPlan(container, config) {
    const purchaseTypeRadios = container.querySelectorAll('.build-a-box__purchase-type-input');
    purchaseTypeRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        updateLineTotals(container, config);
        updateTotals(container, config);
        updateUrlFromWidget(container);
      });
    });
    const planSelect = container.querySelector('.build-a-box__plan-select[data-selling-plan-select]');
    if (planSelect) {
      planSelect.addEventListener('change', () => {
        updateLineTotals(container, config);
        updateTotals(container, config);
        updateUrlFromWidget(container);
      });
    }
  }

  function addToCart(container, config) {
    const addBtn = container.querySelector('[data-add-to-cart]');
    const errorEl = container.querySelector('[data-error]');
    const btnText = container.querySelector('.build-a-box__add-button-text');
    const spinner = container.querySelector('.build-a-box__add-button-spinner');
    const sellingPlanIdRaw = getSelectedSellingPlanId(container);
    const sellingPlanId = sellingPlanIdRaw ? parseInt(sellingPlanIdRaw, 10) : null;

    const parentProductId = container.dataset.parentProductId || '';
    const parentProductHandle = container.dataset.parentProductHandle || '';
    const boxProperties =
      parentProductId && parentProductHandle
        ? {
            _box: `box-${parentProductId}`,
            _parent_product_handle: parentProductHandle,
            _parent_product_id: String(parentProductId),
          }
        : null;

    const items = [];
    container.querySelectorAll('.build-a-box__qty-input').forEach((input) => {
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) return;
      const variantId = parseInt(input.dataset.variantId, 10);
      const item = { id: variantId, quantity: qty };
      if (sellingPlanId) item.selling_plan = parseInt(sellingPlanId, 10);
      if (boxProperties) item.properties = boxProperties;
      items.push(item);
    });

    if (items.length === 0) return;

    addBtn.disabled = true;
    if (btnText) btnText.classList.add('hidden');
    if (spinner) spinner.classList.remove('hidden');
    if (errorEl) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const addUrl =
      routes.cart_add_url && !routes.cart_add_url.endsWith('.js')
        ? routes.cart_add_url
        : '/cart/add';

    function reenableButton() {
      addBtn.disabled = false;
      if (btnText) btnText.classList.remove('hidden');
      if (spinner) spinner.classList.add('hidden');
    }

    function doAddToCart(index) {
      if (index >= items.length) {
        return Promise.resolve(null);
      }
      const item = items[index];
      const config = (typeof fetchConfig === 'function' ? fetchConfig : window.fetchConfig)('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', item.id);
      formData.append('quantity', item.quantity);
      if (item.selling_plan) formData.append('selling_plan', item.selling_plan);
      if (item.properties) {
        Object.entries(item.properties).forEach(([key, val]) => {
          formData.append('properties[' + key + ']', String(val));
        });
      }
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      return fetch(addUrl, config)
        .then((res) => res.json())
        .then((data) => {
          if (data.status && (data.status !== 200 || data.message)) {
            throw new Error(data.description || data.message || 'Could not add to cart');
          }
          return index + 1 >= items.length ? data : doAddToCart(index + 1);
        });
    }

    if (cart && typeof cart.setActiveElement === 'function' && document.activeElement) {
      cart.setActiveElement(document.activeElement);
    }

    doAddToCart(0)
      .then((response) => {
        if (!response) return;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'build-a-box', cartData: response });
        }
        if (cart && typeof cart.renderContents === 'function' && response.sections) {
          CartPerformance && typeof CartPerformance.measure === 'function'
            ? CartPerformance.measure('add:paint-updated-sections', () => cart.renderContents(response))
            : cart.renderContents(response);
          if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          if (typeof cart.open === 'function') cart.open();
        }
        const cartUrl = (routes.cart_url || '/cart').replace(/\/$/, '') + '.js';
        return fetch(cartUrl, { headers: { Accept: 'application/json' } })
          .then((res) => res.json())
          .then((data) => {
            container.dataset.cartTotal = data.total_price != null ? data.total_price : '';
            updateTotals(container, config);
          })
          .catch(() => {});
      })
      .catch((err) => {
        if (errorEl) {
          errorEl.textContent = err.message || 'Could not add to cart';
          errorEl.classList.remove('hidden');
        }
      })
      .finally(() => {
        reenableButton();
      });
  }

  function updatePaymentImage(container) {
    const paymentBlock = container.querySelector('.build-a-box__payment-image');
    if (!paymentBlock) return;
    const singleImage = paymentBlock.querySelector('.payment-image-single-time-payment');
    const subscriptionImage = paymentBlock.querySelector('.payment-image-subscription');
    const checked = container.querySelector('.build-a-box__purchase-type-input:checked');
    const isSubscription = checked && checked.value === 'subscription';
    if (singleImage) singleImage.style.display = isSubscription ? 'none' : 'block';
    if (subscriptionImage) subscriptionImage.style.display = isSubscription ? 'block' : 'none';
  }

  function bindPaymentImages(container) {
    const paymentBlock = container.querySelector('.build-a-box__payment-image');
    if (!paymentBlock) return;
    const subscriptionImage = paymentBlock.querySelector('.payment-image-subscription');
    if (!subscriptionImage) return;
    container.querySelectorAll('.build-a-box__purchase-type-input').forEach((radio) => {
      radio.addEventListener('change', () => updatePaymentImage(container));
    });
    updatePaymentImage(container);
  }

  function init(container) {
    const config = getConfig(container);
    if (!config) return;

    container.dataset.cartTotal = config.cartTotal != null ? config.cartTotal : '';
    applyUrlToWidget(container);
    updatePaymentImage(container);
    updateRowStates(container);
    updateLineTotals(container, config);
    updateTotals(container, config, true);
    bindQuantity(container, config);
    bindSellingPlan(container, config);
    bindPaymentImages(container);

    const addBtn = container.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        e.preventDefault();
        addToCart(container, config);
      });
    }
  }

  function applyCartTotalToBuildABox(total) {
    const totalCents = total != null && total !== '' ? String(total) : '';
    document.querySelectorAll('.build-a-box').forEach((container) => {
      const config = getConfig(container);
      if (!config) return;
      container.dataset.cartTotal = totalCents;
      updateTotals(container, config);
    });
  }

  function refreshCartTotalAndFreeDelivery(event) {
    const cartData = event && event.cartData;
    const totalFromEvent = cartData && (typeof cartData.total_price === 'number' || typeof cartData.total_price === 'string');
    if (totalFromEvent) {
      applyCartTotalToBuildABox(cartData.total_price);
      return;
    }
    const cartUrl = (routes.cart_url || '/cart').replace(/\/$/, '') + '.js';
    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        applyCartTotalToBuildABox(data.total_price != null ? data.total_price : '');
      })
      .catch(() => {});
  }

  let cartUpdateSubscribed = false;

  function initAll() {
    document.querySelectorAll('.build-a-box').forEach(init);
    if (!cartUpdateSubscribed && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      cartUpdateSubscribed = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartTotalAndFreeDelivery);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (typeof document.documentElement.addEventListener === 'function') {
    document.documentElement.addEventListener('shopify:section:load', () => initAll());
  }
})();
